/**
 * aggregateBars — downsample finer bars into coarser ones (e.g. 1m → 5m).
 *
 * Why this lives here: NT's manual "Export Historical Data" UI only offers
 * `Tick`, `Minute`, `Day`. So a tester who right-click-exports a chart can
 * only produce 1-minute bars, but our trade chart defaults to 5m and offers
 * 15m + 1h. Aggregating on the journal side bridges the gap without forcing
 * the user to fight NT's UI.
 *
 * Bucket alignment: clock-aligned to wall-clock minute multiples in UTC.
 *   - 5m  bucket: starts at minutes :00, :05, :10, :15…
 *   - 15m bucket: :00, :15, :30, :45
 *   - 60m bucket: top of every hour
 * This matches how NT (and every other charting platform) defines its
 * intraday boundaries, so a 09:30 bar from our aggregator and a 09:30 bar
 * from a native NT 5m export should overlay perfectly.
 *
 * Aggregation rules per bucket:
 *   open   = first source bar's open
 *   high   = max of source highs
 *   low    = min of source lows
 *   close  = last source bar's close
 *   volume = sum of source volumes
 *   time   = bucket start (Unix seconds)
 *
 * Source bars must be sorted ascending by time. Output is the same.
 */

/**
 * @param {Array<{time:number,open:number,high:number,low:number,close:number,volume?:number}>} bars
 * @param {number} bucketMin  Target bucket size in minutes
 * @returns {Array<{time:number,open:number,high:number,low:number,close:number,volume:number}>}
 */
export const aggregateBars = (bars, bucketMin) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  if (!Number.isFinite(bucketMin) || bucketMin <= 1) return bars;

  const bucketSec = bucketMin * 60;
  const out = [];
  let cur = null;

  for (const b of bars) {
    const bucketStart = Math.floor(b.time / bucketSec) * bucketSec;
    if (cur && cur.time === bucketStart) {
      // Same bucket — extend the running aggregate
      if (b.high > cur.high) cur.high = b.high;
      if (b.low  < cur.low)  cur.low  = b.low;
      cur.close = b.close;
      cur.volume += (b.volume || 0);
    } else {
      // New bucket — flush previous, start fresh
      if (cur) out.push(cur);
      cur = {
        time: bucketStart,
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
        volume: b.volume || 0,
      };
    }
  }
  if (cur) out.push(cur);
  return out;
};

const TF_MINUTES = {
  '1m': 1, '3m': 3, '5m': 5, '15m': 15, '30m': 30,
  '1h': 60, '60m': 60,
  '2h': 120, '120m': 120,
  '4h': 240, '240m': 240,
};

/**
 * Convert a timeframe string (`1m`, `5m`, `15m`, `1h`) to integer minutes.
 * Returns null for unknown values; callers should treat that as "no
 * aggregation, return source bars as-is".
 */
export const tfToMinutes = (tf) => TF_MINUTES[tf] ?? null;
